import { forwardRef, useEffect, useMemo, useRef, type ComponentPropsWithoutRef } from 'react';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import {
  ActionButton,
  CardHeader,
  Cell,
  DropdownList,
  Group,
  Head,
  HeadRow,
  Info,
  Loader,
  NoData,
  Note,
  Pagination,
  Row,
  SubRow,
  usePageInfo,
  usePagination,
} from './components/index.ts';
import {
  CSS_VAR_SCROLL_LEFT,
  CSS_VAR_SCROLL_TOP,
  CSS_VAR_VISIBLE_HEIGHT,
  CSS_VAR_VISIBLE_WIDTH,
  DATA_ATTR_CAN_SCROLL_LEFT,
  DATA_ATTR_CAN_SCROLL_LEFT_PAST_STICKY,
  DATA_ATTR_CAN_SCROLL_RIGHT,
  DATA_ATTR_CAN_SCROLL_TOP,
  DATA_ATTR_HAS_HORIZONTAL_SCROLLBAR,
  DATA_ATTR_HAS_VERTICAL_SCROLLBAR,
  DATA_ATTR_TABLE_SCROLLABLE,
  DND_CELL_WIDTH_PX,
} from './constants.ts';
import { useTableContext } from './context.tsx';
import { DndOverlay, DndProvider, DndRow, DragCell, DragHandle, useDnd, useDndRow } from './dnd/index.ts';
import { ActionGroup, StyledGrid, StyledWrapper, TableScrollableWrapper, type WrapperProps } from './styled.ts';

const GridInner = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(function GridInner({ children, ...rest }, ref) {
  const { columns, inner, fluid } = useTableContext();

  return (
    <StyledGrid ref={ref} $columns={columns} $inner={inner} $fluid={fluid} role='table' {...rest}>
      {children}
    </StyledGrid>
  );
});

const WrapperComponent = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'> & WrapperProps & { $compact?: boolean }>(
  function WrapperComponent({ $inner, $compact, children, ...rest }, ref) {
    const contextValue = useMemo(() => ({ inner: !!$inner, compact: !!$compact }), [$compact, $inner]);

    return (
      <useTableContext.Provider value={contextValue}>
        <StyledWrapper ref={ref} $inner={$inner} {...rest}>
          {children}
        </StyledWrapper>
      </useTableContext.Provider>
    );
  },
);

function useScrollShadow() {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const bordersRef = useRef({ height: 0, width: 0 });
  const { hasDnd } = useTableContext();

  const cacheBorders = useCallbackRef(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const style = getComputedStyle(el);
    bordersRef.current = {
      height: parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth),
      width: parseFloat(style.borderLeftWidth) + parseFloat(style.borderRightWidth),
    };
  });

  const updateScrollState = useCallbackRef(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const canScrollLeft = el.scrollLeft > 0;
    const canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    const canScrollTop = el.scrollTop > 0;
    const stickyLeftOffset = hasDnd ? DND_CELL_WIDTH_PX : 0;
    const canScrollLeftPastSticky = el.scrollLeft > stickyLeftOffset;
    const { height: borderHeight, width: borderWidth } = bordersRef.current;
    const hasHorizontalScrollbar = el.offsetHeight - el.clientHeight > borderHeight;
    const hasVerticalScrollbar = el.offsetWidth - el.clientWidth > borderWidth;

    el.setAttribute(DATA_ATTR_CAN_SCROLL_LEFT, String(canScrollLeft));
    el.setAttribute(DATA_ATTR_CAN_SCROLL_LEFT_PAST_STICKY, String(canScrollLeftPastSticky));
    el.setAttribute(DATA_ATTR_CAN_SCROLL_RIGHT, String(canScrollRight));
    el.setAttribute(DATA_ATTR_CAN_SCROLL_TOP, String(canScrollTop));
    el.setAttribute(DATA_ATTR_HAS_HORIZONTAL_SCROLLBAR, String(hasHorizontalScrollbar));
    el.setAttribute(DATA_ATTR_HAS_VERTICAL_SCROLLBAR, String(hasVerticalScrollbar));
    el.style.setProperty(CSS_VAR_SCROLL_LEFT, `${el.scrollLeft}px`);
    el.style.setProperty(CSS_VAR_SCROLL_TOP, `${el.scrollTop}px`);
    el.style.setProperty(CSS_VAR_VISIBLE_WIDTH, `${el.clientWidth}px`);
    el.style.setProperty(CSS_VAR_VISIBLE_HEIGHT, `${el.clientHeight}px`);
  });

  // cacheBorders/updateScrollState are useCallbackRef-stabilized: their identity never changes, so
  // this effect runs once (subscribe/observe on mount, clean up on unmount), and each invocation
  // reads the latest closure values — including `hasDnd` — without needing to re-subscribe.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    cacheBorders();
    updateScrollState();

    el.addEventListener('scroll', updateScrollState, { passive: true });
    const observer = new ResizeObserver(() => {
      cacheBorders();
      updateScrollState();
    });
    observer.observe(el);

    return () => {
      el.removeEventListener('scroll', updateScrollState);
      observer.disconnect();
    };
  }, [cacheBorders, updateScrollState]);

  // Recompute when DnD column registration flips hasDnd: the subscribe-once effect above only re-runs
  // on scroll/resize, which may never fire if the wrapper's size is unchanged (e.g. fixed maxHeight),
  // leaving the sticky-shadow threshold (data-can-scroll-left-past-sticky) stale.
  useEffect(() => {
    updateScrollState();
  }, [hasDnd, updateScrollState]);

  return wrapperRef;
}

export interface TableComponentProps extends ComponentPropsWithoutRef<'div'> {
  maxHeight?: string;
}

const TableComponent = forwardRef<HTMLDivElement, TableComponentProps>(function TableComponent({ maxHeight, children, ...rest }, ref) {
  const scrollRef = useScrollShadow();

  return (
    <TableScrollableWrapper ref={scrollRef} $maxHeight={maxHeight} {...{ [DATA_ATTR_TABLE_SCROLLABLE]: '' }}>
      <GridInner ref={ref} {...rest}>
        {children}
      </GridInner>
    </TableScrollableWrapper>
  );
});

export const Table = Object.assign(TableComponent, {
  ActionButton,
  ActionGroup,
  CardHeader,
  Note,
  Cell,
  DndProvider,
  DndRow,
  DndOverlay,
  DragCell,
  DragHandle,
  DropdownList,
  Group,
  Head,
  HeadRow,
  Info,
  Loader,
  NoData,
  Pagination,
  Row,
  SubRow,
  Wrapper: WrapperComponent,
  useDnd,
  useDndRow,
  usePageInfo,
  usePagination,
});
